import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [branding, setBranding] = useState({ logo_url: null, site_name: 'Train In Japan' });
  const [programs, setPrograms] = useState({});
  const [loading, setLoading] = useState(true);
  const [payingBookingId, setPayingBookingId] = useState(null);

  useEffect(() => {
    fetchBookings();
    axios.get(`${API}/branding/logo`).then(res => setBranding(res.data)).catch(err => console.error('Branding error:', err));
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, { withCredentials: true });
      setBookings(response.data);
      
      // Fetch program details for each booking
      const programIds = [...new Set(response.data.map(b => b.course_id))];
      const programPromises = programIds.map(id => axios.get(`${API}/programs/${id}`));
      const programResponses = await Promise.all(programPromises);
      
      const programsMap = {};
      programResponses.forEach(res => {
        programsMap[res.data.id] = res.data;
      });
      setPrograms(programsMap);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handlePayNow = async (booking, paymentType = 'full') => {
    setPayingBookingId(booking.id);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/checkout`,
        { 
          course_id: booking.course_id, 
          origin_url: originUrl, 
          payment_type: paymentType 
        },
        { withCredentials: true }
      );
      
      if (response.data.checkout_url) {
        toast.success('Redirecting to payment...');
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment initiation failed:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to initiate payment';
      toast.error(errorMsg);
      setPayingBookingId(null);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 to-emerald-50">
      {/* Floating Logo - Hidden on mobile to avoid overlap */}
      <div className="hidden md:fixed md:block top-3 left-6 z-[60] cursor-pointer" onClick={() => navigate('/')}>
        <div className="flex flex-col items-center" style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))' }}>
          {branding.logo_url && (
            <div className="w-20 h-20 rounded-full bg-white border-[3px] border-emerald-600 overflow-hidden">
              <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
            </div>
          )}
          <span className="mt-1 text-base font-bold text-slate-800 bg-white/90 px-2 py-0.5 rounded-full" style={{ fontFamily: 'Playfair Display, serif' }}>{branding.site_name}</span>
        </div>
      </div>

      {/* Navigation - Mobile optimized */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 sm:py-3 flex items-center justify-between">
          {/* Mobile: Show clickable site name */}
          <div className="md:hidden flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            {branding.logo_url && (
              <div className="w-8 h-8 rounded-full bg-white border-2 border-emerald-600 overflow-hidden flex-shrink-0">
                <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
              </div>
            )}
            <span className="text-sm font-semibold text-slate-800 truncate" style={{ fontFamily: 'Playfair Display, serif' }}>{branding.site_name}</span>
          </div>
          {/* Desktop: Spacer for floating logo */}
          <div className="hidden md:block w-12"></div>
          <div className="flex items-center gap-3 sm:gap-6">
            <button onClick={() => navigate('/programs')} className="text-sm sm:text-base text-slate-700 hover:text-emerald-800 font-medium transition-colors">Programs</button>
            <Button data-testid="logout-btn" onClick={handleLogout} variant="outline" size="sm" className="text-sm">Logout</Button>
          </div>
        </div>
      </nav>

      {/* Dashboard Content */}
      <div data-testid="student-dashboard" className="max-w-7xl mx-auto px-4 md:px-6 pt-16 sm:pt-20 pb-8 sm:pb-12">
        {/* Profile Section - Mobile optimized */}
        <Card className="mb-6 sm:mb-8">
          <CardContent className="p-4 sm:p-6 md:p-8">
            {/* Mobile Profile */}
            <div className="md:hidden flex items-center gap-4">
              <Avatar className="h-14 w-14 sm:h-16 sm:w-16">
                <AvatarImage src={user?.picture} />
                <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h2 data-testid="user-name" className="text-base sm:text-lg font-bold text-slate-900 truncate" style={{ fontFamily: 'Spectral, serif' }}>{user?.name}</h2>
                <p data-testid="user-email" className="text-sm text-slate-600 truncate">{user?.email}</p>
                <span className="inline-block mt-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Student</span>
              </div>
            </div>
            {/* Desktop Profile */}
            <div className="hidden md:flex items-center gap-6">
              <Avatar className="h-20 w-20">
                <AvatarImage src={user?.picture} />
                <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <h2 data-testid="user-name" className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Spectral, serif' }}>{user?.name}</h2>
                <p data-testid="user-email" className="text-slate-600">{user?.email}</p>
                <span className="inline-block mt-2 text-xs font-semibold text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full">Student</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bookings Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>My Bookings</h2>
            <Button data-testid="browse-programs-btn" onClick={() => navigate('/programs')} className="bg-emerald-700 hover:bg-emerald-800 text-sm sm:text-base" size="sm">Browse More</Button>
          </div>

          {loading ? (
            <div className="text-center py-12 sm:py-16 md:py-20">
              <div className="inline-block animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-emerald-700"></div>
            </div>
          ) : bookings.length === 0 ? (
            <Card>
              <CardContent className="p-8 sm:p-12 text-center">
                <p data-testid="no-bookings-msg" className="text-slate-600 mb-4 text-sm sm:text-base">You haven't made any bookings yet.</p>
                <Button onClick={() => navigate('/programs')} className="bg-emerald-700 hover:bg-emerald-800">Explore Programs</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {bookings.map((booking, idx) => {
                const program = programs[booking.course_id];
                const isUnpaid = booking.payment_status === 'unpaid' || !booking.payment_status;
                const isPaying = payingBookingId === booking.id;
                return (
                  <Card key={booking.id} data-testid={`booking-card-${idx}`} className="hover:shadow-lg transition-shadow">
                    {program && (
                      <div className="h-32 overflow-hidden">
                        <img src={program.image_url} alt={program.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                          booking.payment_status === 'paid' && booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                          booking.payment_status === 'deposit_paid' ? 'bg-blue-100 text-blue-700' :
                          booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {booking.payment_status === 'paid' ? 'Confirmed' : 
                           booking.payment_status === 'deposit_paid' ? 'Deposit Paid' : 
                           booking.status}
                        </span>
                        {booking.payment_status && (
                          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                            booking.payment_status === 'paid' ? 'bg-blue-100 text-blue-700' : 
                            booking.payment_status === 'deposit_paid' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {booking.payment_status === 'paid' ? 'Paid' : 
                             booking.payment_status === 'deposit_paid' ? 'Partial' : 
                             'Unpaid'}
                          </span>
                        )}
                      </div>
                      <CardTitle className="text-xl" style={{ fontFamily: 'Spectral, serif' }}>{program?.title || 'Program'}</CardTitle>
                      <CardDescription>Booked: {new Date(booking.booking_date).toLocaleDateString()}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <p className="text-slate-600"><strong>Student:</strong> {booking.student_name}</p>
                        <p className="text-slate-600"><strong>Email:</strong> {booking.student_email}</p>
                        {booking.amount_paid && <p className="text-slate-600"><strong>Amount Paid:</strong> ¥{Number(booking.amount_paid).toLocaleString()}</p>}
                        {booking.remaining_balance > 0 && (
                          <p className="text-amber-600"><strong>Remaining Balance:</strong> ¥{Number(booking.remaining_balance).toLocaleString()}</p>
                        )}
                        {booking.message && <p className="text-slate-600"><strong>Message:</strong> {booking.message}</p>}
                      </div>
                      
                      {/* Pay Now button for unpaid bookings */}
                      {isUnpaid && booking.status !== 'cancelled' && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-sm text-amber-600 mb-3">⚠️ Payment pending. Complete your payment to confirm this booking.</p>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Button 
                              onClick={() => handlePayNow(booking, 'full')}
                              disabled={isPaying}
                              className="bg-emerald-700 hover:bg-emerald-800 flex-1"
                            >
                              {isPaying ? 'Processing...' : 'Pay Now'}
                            </Button>
                            <Button 
                              onClick={() => handlePayNow(booking, 'deposit')}
                              disabled={isPaying}
                              variant="outline"
                              className="flex-1"
                            >
                              Pay 12% Deposit
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Pay remaining balance for deposit_paid bookings */}
                      {booking.payment_status === 'deposit_paid' && booking.remaining_balance > 0 && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-sm text-amber-600 mb-3">💰 Remaining balance: ¥{Number(booking.remaining_balance).toLocaleString()}</p>
                          <Button 
                            onClick={() => navigate(`/programs/${booking.course_id}`)}
                            className="w-full bg-emerald-700 hover:bg-emerald-800"
                          >
                            Pay Remaining Balance
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
